import { AppIcon, type IconDefinition } from '@/components/ui/app-icon';
import { CLU_COLOURS } from '@/components/ui/clu-colours';
import { CluBird } from '@/components/ui/copilot-icon.component';
import { MARK_COLOURS_BY_KEY } from '@/containers/agents/agent-mark.util';
import { CONNECTORS } from '@/containers/onboarding/connector-logos';
import {
  iconChartBar,
  iconCode,
  iconCoins,
  iconLifeRing,
  iconMagnifyingGlass,
  iconPhone,
  iconUsers,
} from '@/lib/icons';
import { cn } from '@/lib/utils';
import type { AppSumoUpgradeGain } from '@/store/features/appsumo.feature';
import type { ReactNode } from 'react';

/**
 * What each plan includes, as the trial page and every upgrade card show it:
 * a visual over a short label. Mirrors the v4 plan catalog
 * (providers/billing/pricing_v4.py) and the compare-plans table.
 */

export interface PlanTile {
  label: string;
  visual: ReactNode;
  /** A short line under the label, e.g. the plans that have it. */
  note?: string;
  /** Set the label in bold, for the one tile that should stand out. */
  emphasis?: boolean;
}

const MARK = 'h-4 w-4 shrink-0';

export function TileIcon({ icon }: { icon: IconDefinition }) {
  return (
    <AppIcon icon={icon} className='h-[18px] w-[18px] text-foreground/70' />
  );
}

function Logos({ srcs }: { srcs: string[] }) {
  return (
    <span className='flex items-center gap-1'>
      {srcs.map((src) => (
        <img
          key={src}
          src={src}
          alt=''
          className={`${MARK} rounded-[3px] object-contain`}
        />
      ))}
    </span>
  );
}

const {
  slack,
  hubspot,
  salesforce,
  gmail,
  linkedin,
  lemlist,
  notion,
  fireflies,
  fathom,
} = CONNECTORS;

const crm = (
  <span className='flex items-center gap-1.5'>
    <salesforce.Mark className={MARK} />
    <hubspot.Mark className={MARK} />
  </span>
);

const agents = (
  <span className='flex items-center gap-1'>
    {[CLU_COLOURS, MARK_COLOURS_BY_KEY.violet, MARK_COLOURS_BY_KEY.orange].map(
      (colours) => (
        <CluBird
          key={colours.disc}
          colours={colours}
          className='h-[18px] w-[18px]'
        />
      )
    )}
  </span>
);

const dataSources = (
  <Logos
    srcs={[
      '/logos/providers/crustdata.png',
      '/logos/providers/prospeo.png',
      '/logos/providers/findymail.png',
      '/logos/providers/wiza.png',
      '/logos/providers/lead-magic.png',
      '/logos/providers/hunter.png',
    ]}
  />
);

export const PRO_TILES: PlanTile[] = [
  { label: '1,500 credits / seat / mo', visual: <TileIcon icon={iconCoins} /> },
  {
    label: 'Unlimited searches',
    visual: <TileIcon icon={iconMagnifyingGlass} />,
  },
  { label: '5 active agents', visual: agents },
  {
    label: 'Multichannel sequences',
    visual: (
      <span className='flex items-center gap-1.5'>
        <gmail.Mark className={MARK} />
        <linkedin.Mark className={MARK} />
        <AppIcon icon={iconPhone} className='h-3.5 w-3.5 text-emerald-600' />
      </span>
    ),
  },
  { label: '25+ data sources', visual: dataSources },
  { label: 'CRM automation', visual: crm },
  {
    label: 'Clu in Slack',
    visual: <slack.Mark className='h-[18px] w-[18px]' />,
  },
  {
    label: '20+ GTM tools',
    visual: (
      <span className='flex items-center gap-1'>
        {[lemlist, notion, fireflies, fathom].map((tool) => (
          <tool.Mark key={tool.id} className={MARK} />
        ))}
      </span>
    ),
  },
];

export const STARTER_TILES: PlanTile[] = [
  { label: '750 credits / seat / mo', visual: <TileIcon icon={iconCoins} /> },
  {
    label: '1,000 searches / mo',
    visual: <TileIcon icon={iconMagnifyingGlass} />,
  },
  {
    label: 'Email sequences and call tasks',
    visual: (
      <span className='flex items-center gap-1.5'>
        <gmail.Mark className={MARK} />
        <AppIcon icon={iconPhone} className='h-3.5 w-3.5 text-emerald-600' />
      </span>
    ),
  },
  { label: '25+ data sources', visual: dataSources },
  { label: 'Any number of seats', visual: <TileIcon icon={iconUsers} /> },
];

export const ENTERPRISE_TILES: PlanTile[] = [
  { label: 'Custom credits', visual: <TileIcon icon={iconCoins} /> },
  {
    label: 'Unlimited searches',
    visual: <TileIcon icon={iconMagnifyingGlass} />,
  },
  { label: 'Unlimited agents', visual: agents },
  { label: 'CRM automation', visual: crm },
  {
    label: 'API, analytics and reports',
    visual: <TileIcon icon={iconChartBar} />,
  },
  { label: 'Dedicated support', visual: <TileIcon icon={iconLifeRing} /> },
];

/** Email and call steps. AppSumo campaign surfaces show no LinkedIn mark:
 *  the campaign never claims LinkedIn automation (COPY.md, ban risk). */
const emailAndCalls = (
  <span className='flex items-center gap-1.5'>
    <gmail.Mark className={MARK} />
    <AppIcon icon={iconPhone} className='h-3.5 w-3.5 text-emerald-600' />
  </span>
);

/** The visual of each thing Starter or Pro adds to an AppSumo deal (the
 *  offer's `gains`); the words come from the copy deck. */
export const APPSUMO_GAIN_VISUALS: Record<AppSumoUpgradeGain, ReactNode> = {
  search: <TileIcon icon={iconMagnifyingGlass} />,
  clu: <CluBird colours={CLU_COLOURS} className='h-[18px] w-[18px]' />,
  sequences: emailAndCalls,
  agents,
  slack: <slack.Mark className='h-[18px] w-[18px]' />,
  reports: <TileIcon icon={iconChartBar} />,
  crm,
  api: <TileIcon icon={iconCode} />,
};

/**
 * Pro, as a locked screen shows it to an AppSumo-only workspace outside the
 * offer (before it, after it, or with the campaign off): no LinkedIn mark,
 * and no CRM or API tile, which tiers 5 to 7 already have.
 */
export const APPSUMO_PRO_TILES: PlanTile[] = [
  { label: '1,500 credits / seat / mo', visual: <TileIcon icon={iconCoins} /> },
  {
    label: 'Unlimited searches',
    visual: <TileIcon icon={iconMagnifyingGlass} />,
    emphasis: true,
  },
  { label: '5 active agents', visual: agents },
  { label: 'Email sequences and call tasks', visual: emailAndCalls },
  { label: '25+ data sources', visual: dataSources },
  {
    label: 'Clu in Slack',
    visual: <slack.Mark className='h-[18px] w-[18px]' />,
  },
];

/** The tiles of the plan a gate asks for, by its label ("Starter", ...). */
export function tilesForPlan(plan: string): PlanTile[] {
  const key = plan.trim().toLowerCase();
  if (key === 'enterprise') return ENTERPRISE_TILES;
  if (key === 'starter') return STARTER_TILES;
  return PRO_TILES;
}

export const TILE =
  'flex min-h-[96px] flex-col items-center justify-start gap-2.5 rounded-xl px-3 pt-5 pb-4 text-center';

export function PlanTileView({
  tile,
  className,
}: {
  tile: PlanTile;
  className?: string;
}) {
  return (
    <div className={cn(TILE, 'bg-muted/60', className)}>
      <span className='flex h-[18px] items-center'>{tile.visual}</span>
      <span
        className={cn(
          'text-[13px] leading-snug text-balance text-foreground',
          tile.emphasis ? 'font-bold' : 'font-medium'
        )}
      >
        {tile.label}
      </span>
      {tile.note ? (
        <span className='-mt-1.5 text-[11px] leading-4 text-muted-foreground'>
          {tile.note}
        </span>
      ) : null}
    </div>
  );
}
